/* eslint-disable react/no-multi-comp */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Cookies from 'cookies-js';

import ConditionalComponent from 'consumer-utils/components/ConditionalComponent';

import {
    setFollowOrganizerStatus,
    setFollowCollectionStatus,
} from '@eb/destination-utils';

import Button from 'eventbrite_design_system/button/Button';

import {
    GA_GDPR_ACCEPT_ACTION,
    GA_GDPR_REJECT_ACTION,
    GDPR_MODAL_HEADER,
    GDPR_MODAL_DESCRIPTION,
    GDPR_ACCEPT_COPY,
    GDPR_REJECT_COPY,
    NOTIFICATION_COOKIE,
    NOTIFICATION_COOKIE_EXPIRATION_SECONDS,
    SAVE_ORGANIZER_FOLLOW_PREFERENCE_ERROR,
    NOTIFICATION_COOKIE_COLLECTION,
    GDPR_COLLECTION_MODAL_HEADER,
    GDPR_COLLECTION_MODAL_DESCRIPTION,
    GA_GDPR_COLLECTION_ACCEPT_ACTION,
    GA_GDPR_COLLECTION_REJECT_ACTION,
} from '../constants';

const GDPR_TYPE_CONTENT_MAP = {
    collection: {
        cookie: NOTIFICATION_COOKIE_COLLECTION,
        api: setFollowCollectionStatus,
        copy: {
            title: GDPR_COLLECTION_MODAL_HEADER,
            message: GDPR_COLLECTION_MODAL_DESCRIPTION,
        },
        analytics: {
            acceptAction: GA_GDPR_COLLECTION_ACCEPT_ACTION,
            rejectAction: GA_GDPR_COLLECTION_REJECT_ACTION,
        },
    },
    organizer: {
        cookie: NOTIFICATION_COOKIE,
        api: setFollowOrganizerStatus,
        copy: {
            title: GDPR_MODAL_HEADER,
            message: GDPR_MODAL_DESCRIPTION,
        },
        analytics: {
            acceptAction: GA_GDPR_ACCEPT_ACTION,
            rejectAction: GA_GDPR_REJECT_ACTION,
        },
    },
};

const setFollowOptInCookieTo = (isOptedIn, cookie) => {
    Cookies.set(cookie, isOptedIn, {
        path: '/',
        secure: true,
        expires: NOTIFICATION_COOKIE_EXPIRATION_SECONDS,
    });
};

const setFollowPreference = (
    userId,
    isOptedIn,
    handleSetError,
    setFollowApi,
    launchCommSettingsFollowAction,
) => {
    setFollowApi(userId, isOptedIn, launchCommSettingsFollowAction).catch(
        () => {
            handleSetError(SAVE_ORGANIZER_FOLLOW_PREFERENCE_ERROR);
        },
    );
};

const TitleHeader = ({ title }) => (
    <h2
        className="
            eds-dialog__title
            eds-l-mar-top-10
            eds-l-pad-hor-5
            eds-align--center
            eds-text--center
            eds-text-hs
            eds-text-weight--heavy"
        data-spec="eds-dialog__title"
    >
        {title}
    </h2>
);

const MessageParagraph = ({ message }) => (
    <p
        className="
            eds-dialog__message
            eds-l-mar-vert-5
            eds-text-bm
            eds-text--center
            eds-text-color--ui-800
            eds-l-pad-hor-10
        "
    >
        {message}
    </p>
);

class ButtonBar extends PureComponent {
    componentDidMount() {
        this._focusRef(this.okButtonRef);
    }

    _createRef = (name) => (ref) => {
        this[name] = ref;
    };

    _focusRef = (ref) => {
        if (ref) {
            ref.focus();
        }
    };

    render() {
        const {
            primaryType,
            primaryText,
            onPrimaryAction,
            secondaryType,
            secondaryText,
            onSecondaryAction,
            title,
        } = this.props;
        const buttonBarClasses = classNames(
            'eds-dialog__button-bar',
            'eds-align--center',
            'eds-text-bm',
            {
                'eds-dialog__button-bar--with-title': title,
            },
        );

        let secondaryButton = null;

        if (secondaryText) {
            secondaryButton = (
                <div className="eds-dialog__secondary-button eds-l-pad-hor-2">
                    <Button
                        onClick={onSecondaryAction}
                        style={secondaryType}
                        data-spec="eds-dialog__secondary-button"
                    >
                        {secondaryText}
                    </Button>
                </div>
            );
        }

        let component = null;

        if (primaryText) {
            component = (
                <div className="eds-l-mar-top-5 eds-l-mar-bot-10">
                    <div className="eds-dialog__button-bar eds-align--center">
                        <div className={buttonBarClasses}>
                            {secondaryButton}
                            <div className="eds-dialog__primary-button eds-l-pad-hor-2">
                                <Button
                                    onClick={onPrimaryAction}
                                    style={primaryType}
                                    data-spec="eds-dialog__primary-button"
                                    ref={this._createRef('okButtonRef')}
                                >
                                    {primaryText}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return component;
    }
}

export default class GDPRModalContent extends PureComponent {
    static propTypes = {
        userId: PropTypes.string,
        trackGAEvent: PropTypes.func,
        onCloseGDPRModal: PropTypes.func,
        handleSetError: PropTypes.func,
        gdprType: PropTypes.oneOf(['collection', 'organizer']),
        launchCommSettingsFollowAction: PropTypes.bool,
    };

    static defaultProps = {
        gdprType: 'organizer',
    };

    componentWillUnmount() {
        if (Cookies.get(this._getCookie()) === undefined) {
            this._handleReject();
        }
    }

    _getCookie = () => GDPR_TYPE_CONTENT_MAP[this.props.gdprType].cookie;

    _getTracking = () => GDPR_TYPE_CONTENT_MAP[this.props.gdprType].analytics;

    _getApi = () => GDPR_TYPE_CONTENT_MAP[this.props.gdprType].api;

    _handleAccept = () => {
        const {
            handleSetError,
            onCloseGDPRModal,
            userId,
            trackGAEvent,
            launchCommSettingsFollowAction,
        } = this.props;

        if (trackGAEvent) {
            const { acceptAction } = this._getTracking();

            trackGAEvent({
                action: acceptAction,
            });

            setFollowPreference(
                userId,
                true,
                handleSetError,
                this._getApi(),
                launchCommSettingsFollowAction,
            );
            setFollowOptInCookieTo(true, this._getCookie());
            onCloseGDPRModal();
        }
    };

    _handleReject = () => {
        const {
            handleSetError,
            onCloseGDPRModal,
            userId,
            trackGAEvent,
            launchCommSettingsFollowAction,
        } = this.props;

        if (trackGAEvent) {
            const { rejectAction } = this._getTracking();

            trackGAEvent({
                action: rejectAction,
            });

            setFollowPreference(
                userId,
                false,
                handleSetError,
                this._getApi(),
                launchCommSettingsFollowAction,
            );
            setFollowOptInCookieTo(false, this._getCookie());
        }
        onCloseGDPRModal();
    };

    render() {
        const { gdprType } = this.props;
        const { title, message } = GDPR_TYPE_CONTENT_MAP[gdprType].copy;

        return (
            <section>
                <ConditionalComponent shouldRender={title}>
                    <TitleHeader title={title} />
                </ConditionalComponent>
                <ConditionalComponent shouldRender={message}>
                    <MessageParagraph message={message} />
                </ConditionalComponent>
                <ButtonBar
                    onPrimaryAction={this._handleAccept}
                    primaryText={GDPR_ACCEPT_COPY}
                    primaryType="link"
                    onSecondaryAction={this._handleReject}
                    secondaryText={GDPR_REJECT_COPY}
                    secondaryType="link"
                />
            </section>
        );
    }
}
